import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NgOptionComponent,
  NgSelectComponent,
  NgSelectModule,
} from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { IconsModule } from '@progress/kendo-angular-icons';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import {
  devicesOutlineIcon,
  gearIcon,
  questionCircleIcon,
  SVGIcon,
} from '@progress/kendo-svg-icons';
import { Icon } from '@shared/components/icon/icon.enum';
import { AvailablePage } from '@shared/models/components/event-pages-simple';
import { QuestionSimple } from '@shared/models/components/event-question-simple';
import { PreviewMode } from '@shared/models/enums/preview-mode.enum';
import { Language } from '@shared/models/language';

import { NgClass } from '@angular/common';
import { DeviceService } from '@core/services/device.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';

@Component({
  selector: 'app-preview-sidebar',
  imports: [
    NgSelectModule,
    NgSelectComponent,
    NgOptionComponent,
    FormsModule,
    TranslateModule,
    ButtonComponent,
    IconsModule,
    TooltipModule,
    NgClass,
  ],
  templateUrl: './preview-sidebar.component.html',
  styleUrl: './preview-sidebar.component.scss',
})
export class PreviewSidebarComponent implements OnInit {
  @ViewChildren(NgSelectComponent) ngSelects!: QueryList<NgSelectComponent>;
  @Input() languages: Language[] = [];
  @Input() selectedLanguage: string = '';
  @Input() questions: QuestionSimple[] = [];
  @Input() availablePages: AvailablePage[] = [];
  @Input() rulesInText: string = 'false';
  protected readonly PreviewMode = PreviewMode;
  protected readonly devicesOutlineIcon: SVGIcon = devicesOutlineIcon;
  protected readonly gearIcon: SVGIcon = gearIcon;
  protected readonly questionCircleIcon: SVGIcon = questionCircleIcon;

  // Reusable constants for styling
  protected readonly SIDEBAR_TEXT_COLOR = '#c4d5b6';
  protected readonly SIDEBAR_TEXT_ACTIVE_COLOR = '#ffffff';
  protected readonly ICON_SIZE_SMALL = 24;
  protected readonly BUTTON_BASE_CLASSES =
    'sidebar-button border-[#c4d5b6] border-solid border text-[#c4d5b6] text-[14px] h-11 px-4 w-full hover:text-white hover:border-white';

  @Input() previewMode: PreviewMode = PreviewMode.FreeFlow;
  @Output() questionChange = new EventEmitter<string>();
  @Output() deviceChange = new EventEmitter<string>();
  @Output() rulesInTextChange = new EventEmitter<string>();
  @Output() useRulesChange = new EventEmitter<string>();
  @Output() previewModeChange = new EventEmitter<PreviewMode>();

  @Output() languageChange = new EventEmitter<string>();
  @Output() print = new EventEmitter<Event>();
  @Output() goToChange = new EventEmitter<{
    type: 'question' | 'page';
    key: string;
  }>();
  @Output() menuToggle = new EventEmitter<boolean>();

  selectedDevice = 'desktop';
  @Input() useRules: string = 'false';
  Icon = Icon;
  @Input() isMenuOpen = false;
  @Input() forceMobileLayout = false;
  isDesktopMenuCollapsed = false;
  selectedGoTo: { type: 'question' | 'page'; key: string } | null = null;

  private readonly deviceService = inject(DeviceService);
  readonly isMobileOrTablet = this.deviceService.isMobileOrTablet;

  ngOnInit() {
    const saved = localStorage.getItem('previewMenuCollapsed');
    if (saved) {
      this.isDesktopMenuCollapsed = saved === 'true';
    }
  }

  toggleDesktopMenu() {
    this.isDesktopMenuCollapsed = !this.isDesktopMenuCollapsed;
    localStorage.setItem(
      'previewMenuCollapsed',
      String(this.isDesktopMenuCollapsed),
    );
  }

  @HostListener('window:keydown.escape')
  onEscapeKey() {
    this.closeMenu();
  }

  trackByPageName(index: number, page: AvailablePage): string {
    return page.name;
  }

  trackByQuestionId(index: number, question: QuestionSimple): string {
    return question.questionId;
  }

  closeMenu() {
    if (this.isMenuOpen) {
      this.menuToggle.emit(false);
    }
  }

  onPreviewModeChange(value: PreviewMode) {
    this.previewMode = value;
    this.previewModeChange.emit(value);
  }

  onQuestionChange(value: string) {
    if (value) {
      this.questionChange.emit(value);
    }
  }

  onGoToChange(value: { type: 'question' | 'page'; key: string } | null) {
    if (value) {
      this.goToChange.emit(value);
      // Reset selection to show placeholder again
      setTimeout(() => {
        this.selectedGoTo = null;
      }, 0);
    }
  }

  onDeviceChange(value: any) {
    this.deviceChange.emit(value);
  }

  onRulesInTextChange(value: any) {
    this.rulesInTextChange.emit(value);
  }

  onUseRulesChange(value: any) {
    this.useRules = value;
    this.useRulesChange.emit(value);
  }

  onLanguageChange(value: Language) {
    this.languageChange.emit(value.code);
  }

  onCopyLink() {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
  }

  onPrint() {
    this.print.emit();
  }

  private closeAllSelects() {
    this.ngSelects.forEach((select) => select.close());
  }

  protected getPreviewModeLabelKey(mode: PreviewMode): string {
    return mode === PreviewMode.SimulateResponse
      ? 'preview.label.simulateresponse'
      : 'preview.label.freenavigation';
  }

  protected getDeviceLabelKey(device: string): string {
    switch (device) {
      case 'mobile':
        return 'preview.label.mobile';
      case 'tablet':
        return 'preview.label.tablet';
      default:
        return 'preview.label.desktop';
    }
  }

  protected getRulesInTextLabelKey(value: string): string {
    return value === 'true' ? 'preview.label.show' : 'preview.label.hide';
  }

  protected getGoToPageTranslationKey(key: string): string {
    return (
      this.availablePages.find((page) => page.name === key)
        ?.translationKey ?? ''
    );
  }

  protected getGoToQuestionName(key: string): string {
    return (
      this.questions.find((question) => question.questionId === key)?.name ??
      ''
    );
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.data === 'iframe-click') {
      this.closeAllSelects();
    }
  }
}
